import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useDashboard } from '../hooks/useDashboard';
import { usePWA } from '../utils/pwaManager';
import { useAutoSubscribeIfNotSubscribed, usePushNotifications } from '../hooks/usePushNotifications';
import LoadingSpinner from '../components/LoadingSpinner';
import { isCoachOrAdmin } from '../utils/roles';
import { formatDisplayDate, formatTime, addMinutesToTime } from '../utils/timeUtils';
import { formatTimeRange } from '../utils/availabilityUtils';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import { notificationsAPI } from '../services/api';
import SubscriptionBannerHost from '../components/notifications/SubscriptionBannerHost.jsx';
import {
  CalendarIcon,
  ClockIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  BellIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

// Missing Availability Warning Banner Component
const MissingAvailabilityBanner = ({ onNavigateToAvailability }) => {
  return (
    <div className="rounded-lg bg-yellow-50 border border-yellow-200 p-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-yellow-800">Missing Availability</h3>
          <div className="mt-2 text-sm text-yellow-700">
            <p>You haven't set your availability for upcoming training sessions.</p>
          </div>
          <div className="mt-4">
            <div className="-mx-2 -my-1.5 flex">
              <button
                onClick={onNavigateToAvailability}
                className="rounded-md bg-yellow-50 px-2 py-1.5 text-sm font-medium text-yellow-800 hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-yellow-600 focus:ring-offset-2 focus:ring-offset-yellow-50"
              >
                Set Availability
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Unscheduled Sessions Warning Banner Component
const UnscheduledSessionsBanner = ({ sessionCount }) => {
  return (
    <div className="rounded-lg bg-orange-50 border border-orange-200 p-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <ExclamationTriangleIcon className="h-5 w-5 text-orange-400" />
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-orange-800">Unscheduled Sessions</h3>
          <div className="mt-2 text-sm text-orange-700">
            <p>You have {sessionCount} training session(s) that need to be scheduled.</p>
          </div>
          <div className="mt-4">
            <div className="-mx-2 -my-1.5 flex">
              <button
                onClick={() => window.location.href = '/training-sessions'}
                className="rounded-md bg-orange-50 px-2 py-1.5 text-sm font-medium text-orange-800 hover:bg-orange-100 focus:outline-none focus:ring-2 focus:ring-orange-600 focus:ring-offset-2 focus:ring-offset-orange-50"
              >
                View Sessions
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// PWA Installation Banner Component
const PWAInstallBanner = () => {
  const [showBanner, setShowBanner] = useState(true);
  const [showInstallModal, setShowInstallModal] = useState(false);

  const { user } = useAuth();
  const isAuthenticated = !!user;

  const {
    isInStandaloneMode: isInstalled,
    supportsPWA: isSupported,
    shouldShowSetupBanner: shouldShowUI,
    canInstallPWA: hasPrompt,
    installPWA: install,
  } = usePWA(isAuthenticated);

  // Use the push notification hooks for proper subscription management
  const { permission, isSubscribed } = usePushNotifications();

  // Hide banner if all steps are completed
  const allStepsCompleted = isInstalled && permission === 'granted' && isSubscribed;
  if (!showBanner || allStepsCompleted) return null;

  const handleInstall = async () => {
    try {
      const result = await install();

      if (result.success) {
        setShowBanner(false);
      } else if (result.needsManualInstall || result.showInstructions) {
        // Show modal with install instructions
        setShowInstallModal(true);
      }
    } catch (error) {
      console.error('Install failed:', error);
      // Show modal with install instructions as fallback
      setShowInstallModal(true);
    }
  };

  return (
    <>
      <div className="mb-6 border-l-4 border-blue-400 bg-blue-50 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-blue-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
              />
            </svg>
          </div>
          <div className="ml-3 flex-1">
            <p className="text-sm text-blue-700">
              <strong>Install the app!</strong> Get the best experience with faster access, offline
              capabilities, and push notifications.
            </p>
          </div>
          <div className="ml-3 flex flex-shrink-0 gap-2">
            {hasPrompt ? (
              <button
                onClick={handleInstall}
                className="rounded bg-blue-600 px-3 py-1 text-xs font-medium text-white transition-colors duration-200 hover:bg-blue-700"
              >
                Install
              </button>
            ) : (
              <button
                onClick={() => setShowInstallModal(true)}
                className="rounded bg-blue-600 px-3 py-1 text-xs font-medium text-white transition-colors duration-200 hover:bg-blue-700"
              >
                How to Install
              </button>
            )}
            <button
              onClick={() => setShowBanner(false)}
              className="px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-800"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>

      <PWAInstallModal isOpen={showInstallModal} onClose={() => setShowInstallModal(false)} />
    </>
  );
};

// PWA Installation Modal Component
const PWAInstallModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg
                className="h-6 w-6 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-gray-900">Install Rowing Planner App</h3>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          <p className="mb-4 text-gray-600">
            Install the Rowing Planner app on your device for a better experience:
          </p>

          <div className="space-y-4">
            <div className="rounded-lg bg-blue-50 p-4">
              <div className="flex items-start">
                <div className="w-20 flex-shrink-0">
                  <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                    Chrome
                  </span>
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm text-gray-700">
                    Click the install icon in the address bar, or use the three-dot menu and select
                    "Install app".
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg bg-green-50 p-4">
              <div className="flex items-start">
                <div className="w-20 flex-shrink-0">
                  <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                    Safari
                  </span>
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm text-gray-700">
                    Tap the share button and select "Add to Home Screen" from the menu.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg bg-orange-50 p-4">
              <div className="flex items-start">
                <div className="w-20 flex-shrink-0">
                  <span className="inline-flex items-center rounded-full bg-orange-100 px-2.5 py-0.5 text-xs font-medium text-orange-800">
                    Firefox
                  </span>
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm text-gray-700">
                    Look for the install option in the three-line menu, or check the address bar for
                    install prompts.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 rounded-lg bg-green-50 p-4">
              <div className="flex items-start">
                <CheckCircleIcon className="mr-3 mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
                <div>
                  <p className="text-sm font-medium text-green-800">Why install?</p>
                  <p className="mt-1 text-sm text-green-700">
                    Get faster access, offline capabilities, and push notifications for schedule
                    updates.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex justify-end border-t border-gray-200 p-6">
          <button
            onClick={onClose}
            className="rounded-lg bg-blue-600 px-6 py-2 font-medium text-white transition-colors hover:bg-blue-700"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

// Current Week Availability Component
const CurrentWeekAvailability = ({ availabilitySlots }) => {
  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  if (!availabilitySlots || availabilitySlots.length === 0) {
    return (
      <div className="rounded-lg bg-white p-6 shadow">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">This Week's Availability</h2>
          <CalendarIcon className="h-5 w-5 text-gray-400" />
        </div>
        <div className="mt-4">
          <p className="text-sm text-gray-500">No availability set for this week</p>
        </div>
      </div>
    );
  }

  // Group availability by day and merge consecutive slots
  const availabilityByDay = availabilitySlots.reduce((acc, slot) => {
    const dayName = daysOfWeek[slot.day_of_week];
    if (!acc[dayName]) {
      acc[dayName] = [];
    }
    acc[dayName].push(slot);
    return acc;
  }, {});

  // Function to merge consecutive time slots
  const mergeConsecutiveSlots = (slots) => {
    if (!slots || slots.length === 0) return [];

    // Sort slots by start time
    const sortedSlots = [...slots].sort((a, b) => {
      const timeA = new Date(`2000-01-01T${a.start_time}`);
      const timeB = new Date(`2000-01-01T${b.start_time}`);
      return timeA - timeB;
    });

    const mergedSlots = [];
    let currentBlock = { ...sortedSlots[0] };

    for (let i = 1; i < sortedSlots.length; i++) {
      const currentSlot = sortedSlots[i];
      const currentEndTime = new Date(`2000-01-01T${currentBlock.end_time}`);
      const nextStartTime = new Date(`2000-01-01T${currentSlot.start_time}`);

      // Check if slots are consecutive (end time of current equals start time of next)
      if (currentEndTime.getTime() === nextStartTime.getTime()) {
        // Merge the slots
        currentBlock.end_time = currentSlot.end_time;
      } else {
        // Not consecutive, save current block and start new one
        mergedSlots.push(currentBlock);
        currentBlock = { ...currentSlot };
      }
    }

    // Add the last block
    mergedSlots.push(currentBlock);
    return mergedSlots;
  };

  return (
    <div className="rounded-lg bg-white p-6 shadow">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">This Week's Availability</h2>
        <CalendarIcon className="h-5 w-5 text-gray-400" />
      </div>
      <div className="mt-4">
        <div className="space-y-3">
          {daysOfWeek.map((day) => {
            const daySlots = availabilityByDay[day] || [];
            const mergedSlots = mergeConsecutiveSlots(daySlots);

            return (
              <div key={day} className="flex items-center justify-between border-b border-gray-100 pb-2 last:border-b-0">
                <span className="text-sm font-medium text-gray-700 w-24">{day}</span>
                <div className="flex-1 ml-4">
                  {mergedSlots.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {mergedSlots.map((slot, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {formatTimeRange(slot.start_time, slot.end_time)}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">No availability</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Enhanced Upcoming Sessions Component
const EnhancedUpcomingSessions = ({ sessions }) => {
  if (!sessions || sessions.length === 0) {
    return (
      <div className="rounded-lg bg-white p-6 shadow">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Upcoming Sessions</h2>
          <CalendarIcon className="h-5 w-5 text-gray-400" />
        </div>
        <div className="mt-4">
          <p className="text-sm text-gray-500">No upcoming sessions</p>
        </div>
      </div>
    );
  }

  const getSessionStatus = (session) => {
    if (session.session_type === 'scheduled') {
      return { label: 'Scheduled', color: 'bg-blue-100 text-blue-800' };
    }
    if (session.session_type === 'unscheduled') {
      return { label: 'Unscheduled', color: 'bg-gray-100 text-gray-800' };
    }
    if (session.status === 'completed') return { label: 'Completed', color: 'bg-green-100 text-green-800' };
    if (session.status === 'scheduled') return { label: 'Scheduled', color: 'bg-blue-100 text-blue-800' };
    if (session.status === 'partially-scheduled') {
      return { label: 'Partially Scheduled', color: 'bg-yellow-100 text-yellow-800' };
    }
    return { label: 'Unscheduled', color: 'bg-gray-100 text-gray-800' };
  };

  const formatSessionTime = (session) => {
    const start = session.start_time ?? session.default_start_time;
    const end = session.end_time;
    if (start && end) {
      return `${formatTime(start)} - ${formatTime(end)}`;
    }
    if (start) {
      const startFmt = formatTime(start);
      if (session.duration) {
        return `${startFmt} - ${addMinutesToTime(startFmt, session.duration)}`;
      }
      return startFmt;
    }
    return 'Time TBD';
  };

  return (
    <div className="rounded-lg bg-white p-6 shadow">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Upcoming Sessions</h2>
        <CalendarIcon className="h-5 w-5 text-gray-400" />
      </div>
      <div className="mt-4">
        <div className="space-y-4">
          {sessions.slice(0, 5).map((session) => {
            const status = getSessionStatus(session);
            return (
              <div key={session.id} className="border-l-4 border-blue-200 pl-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-900">{session.title}</h3>
                    <div className="mt-1 flex items-center space-x-4 text-xs text-gray-500">
                      <div className="flex items-center">
                        <CalendarIcon className="mr-1 h-3 w-3" />
                        {formatDisplayDate(new Date(session.date))}
                      </div>
                      <div className="flex items-center">
                        <ClockIcon className="mr-1 h-3 w-3" />
                        {formatSessionTime(session)}
                      </div>
                      {session.location && (
                        <div className="flex items-center">
                          <UserGroupIcon className="mr-1 h-3 w-3" />
                          {session.location}
                        </div>
                      )}
                    </div>
                  </div>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
                    {status.label}
                  </span>
                </div>
                {session.crew_assignments && session.crew_assignments.length > 0 && (
                  <div className="mt-2 text-xs text-gray-500">
                    Crews:{' '}
                    {session.crew_assignments
                      .map((a) => a.crew?.name)
                      .filter(Boolean)
                      .join(', ')}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {sessions.length > 5 && (
          <div className="mt-4 text-center">
            <button
              onClick={() => window.location.href = '/training-sessions'}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              View all {sessions.length} sessions →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Notifications Component
const NotificationsSection = ({ dashboardNotificationsCount }) => {
  const queryClient = useQueryClient();

  const { data: notifications, isLoading } = useQuery(
    ['notifications', 'dashboard'],
    () => notificationsAPI.getNotifications(5, 0, false),
    {
      staleTime: 2 * 60 * 1000, // 2 minutes
    }
  );

  // Use dashboard notifications count as fallback if API call fails
  const unreadCount = dashboardNotificationsCount || 0;

  // Mark notification as read mutation
  const markAsReadMutation = useMutation(
    (notificationId) => notificationsAPI.markAsRead(notificationId),
    {
      onSuccess: () => {
        // Invalidate notifications queries to refresh the data
        queryClient.invalidateQueries(['notifications']);
        queryClient.invalidateQueries(['notifications', 'unread-count']);
      },
      onError: (error) => {
        console.error('Failed to mark notification as read:', error);
      },
    }
  );

  const handleNotificationClick = async (notification) => {
    // Mark as read if not already read
    if (!notification.is_read) {
      try {
        await markAsReadMutation.mutateAsync(notification.id);
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    }

    // Navigate to the action URL if available
    if (notification.action_url) {
      window.location.href = notification.action_url;
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'session_reminder':
      case 'schedule_reminder':
        return <CalendarIcon className="h-4 w-4 text-blue-500" />;
      case 'availability_reminder':
        return <ClockIcon className="h-4 w-4 text-yellow-500" />;
      case 'session_scheduled':
      case 'session_rescheduled':
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case 'schedule_change':
        return <ExclamationTriangleIcon className="h-4 w-4 text-orange-500" />;
      default:
        return <BellIcon className="h-4 w-4 text-gray-500" />;
    }
  };

  if (isLoading) {
    return (
      <div className="rounded-lg bg-white p-6 shadow">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Recent Notifications</h2>
          <BellIcon className="h-5 w-5 text-gray-400" />
        </div>
        <div className="mt-4">
          <div className="animate-pulse space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-4 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-white p-6 shadow">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Recent Notifications</h2>
        <div className="flex items-center space-x-2">
          {unreadCount > 0 && (
            <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-red-100 bg-red-600 rounded-full">
              {unreadCount}
            </span>
          )}
          <BellIcon className="h-5 w-5 text-gray-400" />
        </div>
      </div>
      <div className="mt-4">
        {notifications && notifications.length > 0 ? (
          <div className="space-y-3">
            {notifications.slice(0, 3).map((notification) => (
              <div
                key={notification.id}
                className={`flex items-start space-x-3 p-3 rounded-lg cursor-pointer transition-colors hover:bg-gray-100 ${notification.is_read ? 'bg-gray-50' : 'bg-blue-50'
                  } ${notification.action_url ? 'hover:bg-blue-100' : ''}`}
                onClick={() => handleNotificationClick(notification)}
                title={notification.action_url ? 'Click to view details' : ''}
              >
                <div className="flex-shrink-0 mt-0.5">
                  {getNotificationIcon(notification.notification_type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">{notification.title}</p>
                  <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {new Date(notification.created_at).toLocaleDateString()} at {new Date(notification.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                <div className="flex-shrink-0 flex items-center space-x-2">
                  {!notification.is_read && (
                    <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                  )}
                  {notification.action_url && (
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No recent notifications</p>
        )}
        <div className="mt-4 text-center">
          <button
            onClick={() => window.location.href = '/notifications'}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            View all notifications →
          </button>
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const { dashboardData, isLoading, error } = useDashboard();

  // Auto-subscribe to push notifications if not already subscribed
  useAutoSubscribeIfNotSubscribed(!!user, 3000);

  // Global banner host now manages subscription prompts

  const handleNavigateToAvailability = () => {
    window.location.href = '/availability';
  };

  if (isLoading) {
    return <LoadingSpinner text="Loading dashboard..." />;
  }

  if (error) {
    return (
      <div className="py-12 text-center">
        <p className="text-red-600">Failed to load dashboard data</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Welcome back! Here's an overview of your rowing activities.
        </p>
      </div>

      {/* Subscription Banner - moved from App.jsx to Dashboard */}
      <SubscriptionBannerHost />

      {/* PWA Installation Banner - subtle prompt within dashboard */}
      <PWAInstallBanner />

      {/* Missing Availability Warning Banner */}
      {dashboardData?.has_missing_availability && (
        <MissingAvailabilityBanner onNavigateToAvailability={handleNavigateToAvailability} />
      )}

      {/* Coach Unscheduled Sessions Warning Banner */}
      {isCoachOrAdmin(user) && dashboardData?.unscheduled_sessions_count > 0 && (
        <UnscheduledSessionsBanner sessionCount={dashboardData.unscheduled_sessions_count} />
      )}


      {/* Main Content - Enhanced Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Current Week Availability */}
        <CurrentWeekAvailability availabilitySlots={dashboardData?.recent_availability} />

        {/* Enhanced Upcoming Sessions */}
        <EnhancedUpcomingSessions sessions={dashboardData?.upcoming_sessions} />

        {/* Notifications Section */}
        <NotificationsSection dashboardNotificationsCount={dashboardData?.notifications_count} />

        {/* Recent Activity - Keep existing but enhance if needed */}
        <div className="rounded-lg bg-white p-6 shadow">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
            <UserGroupIcon className="h-5 w-5 text-gray-400" />
          </div>
          <div className="mt-4">
            {dashboardData?.recent_activity?.length > 0 ? (
              <div className="space-y-3">
                {dashboardData.recent_activity.slice(0, 3).map((activity, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                      <p className="text-xs text-gray-500">{activity.description}</p>
                    </div>
                    <span className="text-xs text-gray-400">
                      {new Date(activity.timestamp).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No recent activity</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
